/**
 * @fileoverview Página de autenticação (login e registro).
 *
 * Suporta login com email/senha via Supabase Auth, toggle entre
 * login e cadastro, aceite de termos, e redirecionamento automático
 * para /workspaces após autenticação. Persiste "lembrar-me" via localStorage.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useStore } from '@/store/useStore';
import { Mail, KeyRound, Eye, EyeOff, Moon, Sun } from 'lucide-react';
import logoWhite from '@/assets/logo-light.svg';
import logoDark from '@/assets/logo-dark.svg';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { STORAGE_KEYS } from '@/lib/constants';
import { TermsDialog } from '@/components/TermsDialog';
import { isSafeRedirect, safeRedirectOr } from '@/lib/safeRedirect';
import { oauthErrorHelp } from '@/lib/oauthErrorMessage';

export default function Auth() {
  const { theme, toggleTheme } = useStore();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [oauthError, setOauthError] = useState<{ description: string; code: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsDialogType, setTermsDialogType] = useState<'terms' | 'privacy' | null>(null);
  const [resetMode, setResetMode] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  // Recovery mode = user landed here from a "reset password" email link.
  // Supabase JS auto-creates a session and fires PASSWORD_RECOVERY before
  // SIGNED_IN. We block the auto-redirect so the user can actually set a
  // new password instead of just being logged in with the temporary token.
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    const getRedirect = () => {
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      const pending = localStorage.getItem(STORAGE_KEYS.PENDING_INVITE);
      // Validate pending invite path the same way as the redirect query param.
      // Without this, an XSS injecting a non-internal value into localStorage
      // could redirect the user off-site after login.
      if (pending) {
        localStorage.removeItem(STORAGE_KEYS.PENDING_INVITE);
        if (isSafeRedirect(pending)) return pending;
      }
      // A regra antiga ("começa com / e não com //") deixava passar
      // `/\evil.com`: o navegador normaliza a barra invertida e o valor vira
      // protocolo-relativo, levando para fora do site. Ver lib/safeRedirect.
      return safeRedirectOr(redirect);
    };

    // Erro do provedor OAuth: o GoTrue volta para o site com
    // `#error=…&error_description=…` e o supabase-js engole em silêncio —
    // para a pessoa parecia que "Entrar com Google" só recarregava a página.
    // O ProtectedRoute repassa o hash para cá; mostramos o motivo e limpamos.
    try {
      const h = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const desc = h.get('error_description') || h.get('error');
      if (desc) {
        const code = h.get('error_code');
        const limpo = desc.replace(/\+/g, ' ');
        setOauthError({ description: limpo, code });
        // O toast some; o bloco abaixo do formulário fica. Aqui vai só o
        // essencial — a orientação completa é lida com calma na tela.
        toast.error('Não foi possível entrar com o Google', { description: limpo, duration: 12000 });
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch { /* hash malformado: ignora */ }

    // Detect recovery flow upfront — Supabase puts the recovery token in the
    // URL hash and the JS client consumes it asynchronously, so we also keep
    // listening for PASSWORD_RECOVERY in onAuthStateChange below.
    const hashHasRecovery = window.location.hash.includes('type=recovery');
    const queryHasReset = new URLSearchParams(window.location.search).get('reset') === 'true';
    if (hashHasRecovery || queryHasReset) {
      setRecoveryMode(true);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
        return; // Do not redirect — show the new password form
      }
      if (session) {
        if (recoveryMode || hashHasRecovery || queryHasReset) {
          // User just landed from a reset link. Don't redirect on this implicit
          // SIGNED_IN — wait for them to submit the new password.
          return;
        }
        // Auto-accept pending workspace invites
        if (event === 'SIGNED_IN') {
          // Clear stale workspace/project IDs from previous user session
          localStorage.removeItem(STORAGE_KEYS.ACTIVE_WORKSPACE_ID);
          localStorage.removeItem(STORAGE_KEYS.ACTIVE_PROJECT_ID);
          localStorage.removeItem(STORAGE_KEYS.ACTIVE_PROJECT_TYPE);
          useStore.setState({ activeWorkspaceId: '', activeProjectId: '', activeProjectType: 'crm' as any });
        }
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          const userEmail = session.user.email;
          if (userEmail) {
            try {
              const { error: rpcError } = await supabase.rpc('accept_pending_workspace_invites', {
                _user_id: session.user.id,
                _email: userEmail,
              });
              if (rpcError) {
                toast.error(`Erro ao aceitar convite: ${rpcError.message}`);
              }
            } catch (e: unknown) {
              toast.error(e instanceof Error ? e.message : 'Erro inesperado ao processar convites.');
            }
          }
        }
        navigate(getRedirect());
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      // If the URL signals recovery, hold the user on the new-password form
      // even if a session is already established by the recovery token.
      if (session && !hashHasRecovery && !queryHasReset) navigate(getRedirect());
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }
    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Senha atualizada com sucesso!');
      setRecoveryMode(false);
      setNewPassword('');
      // Clean URL artifacts (hash + ?reset=true) before redirect
      window.history.replaceState({}, '', '/auth');
      navigate('/workspaces');
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      const map: Record<string, string> = {
        'weak_password': 'A senha é muito fraca. Use ao menos 8 caracteres com letras e números.',
        'same_password': 'A nova senha deve ser diferente da atual.',
      };
      toast.error(map[code ?? ''] ?? 'Erro ao atualizar senha. Tente solicitar o link novamente.');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success('Login realizado com sucesso!');
      } else {
        if (!acceptedTerms) {
          toast.error('Você deve aceitar os termos para continuar.');
          setLoading(false);
          return;
        }
        // Stronger requirement on signup than on login: we don't want to
        // break logins for users with old 6-char passwords, but new accounts
        // should meet a more modern bar (NIST/OWASP recommend 12+).
        if (password.length < 12) {
          toast.error('A senha deve ter pelo menos 12 caracteres.');
          setLoading(false);
          return;
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: name,
              lgpd_consent_at: new Date().toISOString(),
            },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        toast.success('Conta criada! Verifique seu email para confirmar.');
      }
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      const msg: Record<string, string> = {
        'invalid_credentials': 'Email ou senha incorretos.',
        'email_not_confirmed': 'Confirme seu email antes de entrar.',
        'user_already_exists': 'Já existe uma conta com este email.',
        'weak_password': 'A senha deve ter pelo menos 6 caracteres.',
        'over_email_send_rate_limit': 'Muitas tentativas. Aguarde alguns minutos.',
      };
      toast.error(msg[code ?? ''] ?? 'Erro ao autenticar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email.trim()) {
      toast.error('Informe seu email para recuperar a senha.');
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/auth?reset=true',
      });
      if (error) throw error;
      toast.success('Email de recuperação enviado! Verifique sua caixa de entrada.');
    } catch (error: unknown) {
      toast.error('Erro ao enviar email de recuperação. Tente novamente.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
    <div className="min-h-screen flex bg-background">
      {/* Left Panel - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center" style={{ background: 'linear-gradient(135deg, hsl(226, 100%, 51%) 0%, hsl(226, 80%, 35%) 50%, hsl(220, 20%, 10%) 100%)' }}>
        {/* Animated blue glow */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 50%, hsla(226, 100%, 60%, 0.3) 0%, transparent 60%), radial-gradient(ellipse at 70% 30%, hsla(200, 80%, 50%, 0.2) 0%, transparent 50%)',
          animation: 'pulseGlow 4s ease-in-out infinite',
        }} />

        <div className="relative z-10 p-12 max-w-md">
          {/* Floating metrics card */}
          <div className="bg-card/10 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-10 shadow-2xl" style={{ animation: 'fadeSlideUp 0.8s ease-out' }}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-white/70 uppercase tracking-widest">Métricas em Tempo Real</p>
              <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
            </div>
            <div className="h-px bg-white/10 mb-4" />
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-xs text-white font-bold">G</span>
              </div>
              <div className="h-px w-6 bg-white/20" />
              <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-xs text-white font-bold">M</span>
              </div>
            </div>
            <p className="text-xs text-white/60 mb-1">ROI Total</p>
            <p className="text-4xl font-bold text-white tracking-tight" style={{ fontFamily: "'Nexa Bold', sans-serif" }}>+1,245%</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-success font-semibold bg-success/20 px-2 py-0.5 rounded-full">+32.5%</span>
            </div>
            <div className="flex items-end gap-1 mt-4 h-12">
              {[40, 55, 35, 70, 60, 80, 75, 90].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm bg-white/30" style={{ height: `${h}%`, animation: `fadeSlideUp 0.4s ease-out ${i * 60}ms both` }} />
              ))}
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'Nexa Bold', sans-serif" }}>
            Insights reais para decisões inteligentes.
          </h2>
          <p className="text-sm text-white/60 leading-relaxed">
            Conecte suas contas e tenha uma visão unificada de todo o seu desempenho de marketing.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 lg:px-16 relative">
        {/* Theme & Settings toggles */}
        <div className="absolute top-6 right-6 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-muted-foreground hover:text-foreground" aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}>
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>

        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <img
            src={isDark ? logoWhite : logoDark}
            alt="Legacy Insights"
            className="h-8 object-contain"
          />

          {/* Title */}
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: "'Nexa Bold', sans-serif" }}>
              {recoveryMode ? 'Definir Nova Senha' : resetMode ? 'Recuperar Senha' : isLogin ? 'Entrar na Plataforma' : 'Criar Conta'}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {recoveryMode ? 'Defina uma nova senha para sua conta. Você precisará fazer login novamente.' : resetMode ? 'Informe seu email para receber o link de recuperação.' : isLogin ? 'Bem-vindo de volta! Insira seus dados para continuar.' : 'Preencha seus dados para criar sua conta.'}
            </p>
          </div>

          {recoveryMode ? (
            <form onSubmit={handleSetNewPassword} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-foreground">Nova Senha</Label>
                <div className="relative mt-2">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={128}
                    className="h-12 rounded-xl pl-11 pr-11"
                    required
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Use uma senha forte com letras, números e pelo menos 8 caracteres.
                </p>
              </div>
              <Button type="submit" className="w-full h-12 rounded-xl text-base font-semibold bg-secondary hover:bg-secondary/90 text-secondary-foreground" disabled={updatingPassword}>
                {updatingPassword ? 'Atualizando...' : 'Definir nova senha'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={async () => {
                await supabase.auth.signOut();
                setRecoveryMode(false);
                window.history.replaceState({}, '', '/auth');
              }}>
                Cancelar e voltar ao login
              </Button>
            </form>
          ) : resetMode ? (
            <div className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-foreground">Endereço de E-mail</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="exemplo@legacy.com"
                    className="h-12 rounded-xl pl-11"
                    required
                  />
                </div>
              </div>
              <Button
                className="w-full h-12 rounded-xl text-base font-semibold bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                disabled={resetLoading}
                onClick={handleResetPassword}
              >
                {resetLoading ? 'Enviando...' : 'Enviar link de recuperação'}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => setResetMode(false)}>
                Voltar ao login
              </Button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label className="text-sm font-medium text-foreground">Nome Completo</Label>
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Seu nome"
                  className="mt-2 h-12 rounded-xl pl-4"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <Label className="text-sm font-medium text-foreground">Endereço de E-mail</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="exemplo@legacy.com"
                  className="h-12 rounded-xl pl-11"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-foreground">Sua Senha</Label>
              <div className="relative mt-2">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={isLogin ? '••••••••' : 'Mínimo 12 caracteres'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  maxLength={128}
                  className="h-12 rounded-xl pl-11 pr-11"
                  required
                  minLength={isLogin ? 6 : 12}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className="flex items-center justify-end">
                <button type="button" className="text-sm text-secondary hover:text-secondary/80 font-medium" onClick={() => setResetMode(true)}>Esqueceu a senha?</button>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(!!v)}
                    id="terms"
                    className="mt-0.5"
                  />
                  <label htmlFor="terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                    Li e aceito os{' '}
                    <button type="button" onClick={() => setTermsDialogType('terms')} className="text-secondary hover:underline font-medium">Termos de Uso</button>
                    {' '}e a{' '}
                    <button type="button" onClick={() => setTermsDialogType('privacy')} className="text-secondary hover:underline font-medium">Política de Privacidade</button>
                    {' '}(LGPD).
                  </label>
                </div>
              </div>
            )}

            <Button type="submit" className="w-full h-12 rounded-xl text-base font-semibold bg-secondary hover:bg-secondary/90 text-secondary-foreground" disabled={loading || (!isLogin && !acceptedTerms)}>
              {loading ? 'Carregando...' : isLogin ? 'Entrar agora' : 'Criar conta'}
            </Button>
          </form>
          )}

          {oauthError && (() => {
            const ajuda = oauthErrorHelp(oauthError.description, oauthError.code);
            return (
              <div role="alert" className="text-xs rounded-lg border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 space-y-1">
                <p><strong>Não foi possível entrar com o Google:</strong> {ajuda.detalhe}</p>
                <p className="text-destructive/90">{ajuda.acao}</p>
                {oauthError.code && (
                  <p className="text-[10px] text-destructive/70">Código: {oauthError.code}</p>
                )}
              </div>
            );
          })()}

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-background px-4 text-muted-foreground">Ou continue com</span></div>
          </div>

          {/* Social Login.
             Note: when in signup mode (!isLogin), we require the LGPD
             checkbox before redirecting to the OAuth provider. Otherwise
             users could create accounts without consent (the provider
             flow doesn't show our terms checkbox). */}
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="w-full h-11 rounded-xl gap-2 text-sm"
              disabled={!isLogin && !acceptedTerms}
              onClick={async () => {
                if (!isLogin && !acceptedTerms) {
                  toast.error('Você deve aceitar os termos para criar uma conta.');
                  return;
                }
                // `prompt: select_account` obriga o Google a mostrar o seletor.
                // Sem ele o Google reaproveita em silêncio a conta em que o
                // navegador já está logado — e quem tem duas (a corporativa do
                // Workspace e a pessoal) nunca consegue escolher. Foi o caso de
                // 23/09/2026: o erro aparecia "antes de abrir" porque não havia
                // tela nenhuma para abrir, e a pessoa não tinha como corrigir.
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo: window.location.origin,
                    queryParams: { prompt: 'select_account' },
                  },
                });
                if (error) toast.error('Não foi possível iniciar o login', { description: error.message });
              }}>
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Entrar com Google
            </Button>
            <Button
              className="w-full h-11 rounded-xl gap-2 text-sm bg-foreground text-background hover:bg-foreground/90"
              disabled={!isLogin && !acceptedTerms}
              onClick={async () => {
                if (!isLogin && !acceptedTerms) {
                  toast.error('Você deve aceitar os termos para criar uma conta.');
                  return;
                }
                const { error } = await supabase.auth.signInWithOAuth({ provider: 'apple', options: { redirectTo: window.location.origin } });
                if (error) toast.error('Não foi possível iniciar o login', { description: error.message });
              }}>
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              Entrar com Apple
            </Button>
          </div>

          {/* Toggle */}
          <p className="text-center text-sm text-muted-foreground">
            {isLogin ? 'Não tem uma conta? ' : 'Já tem uma conta? '}
            <button onClick={() => setIsLogin(!isLogin)} className="text-secondary hover:text-secondary/80 font-semibold">
              {isLogin ? 'Cadastre-se' : 'Entrar'}
            </button>
          </p>
        </div>
      </div>
    </div>

    {/* Terms/Privacy Dialog */}
    {termsDialogType && (
      <TermsDialog open={!!termsDialogType} onClose={() => setTermsDialogType(null)} type={termsDialogType} />
    )}
    </>
  );
}
